import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { apiService } from '../../services/ApiService';
import type { AncestorThreads, ThreadListing, ThreadSummary } from '../../types/agent';
import { Header, GlyphButton } from './Header';
import { AgentTag } from './agentLabel';
import { Delayed } from './motion';
import { Spinner } from './Spinner';

type GroupKey = 'today' | 'yesterday' | 'lastWeek' | 'earlier';
const GROUP_ORDER: GroupKey[] = ['today', 'yesterday', 'lastWeek', 'earlier'];

/** Full-screen history (design 1c): search, threads grouped by day, open / rename / delete,
 *  language + sign-out in the footer. Rename and delete are inline (an input, a two-step confirm):
 *  the Office webview blocks window.prompt / window.confirm, so native dialogs never return.
 *
 *  A thread belongs to one workbook (sculpt-api PLAN-workbook-identity.md §6). "This workbook" lists its own
 *  threads, then each earlier version's threads from before the fork, read-only; "All my conversations" lists
 *  every thread with its file, writable only in its own workbook. A read-only thread opens read-only, and the
 *  pane offers to continue it here. */
type RowState = { kind: 'rename'; id: string; value: string } | { kind: 'delete'; id: string } | null;
type Tab = 'workbook' | 'all';
export function ThreadHistory({ workbookId, workbookName, currentThreadId, onOpen, onNew, onClose, onSignOut }: { workbookId: string | null; workbookName: string | null; currentThreadId: string | null; /** Resolves once the thread is loaded (the pane then leaves the history). */ onOpen: (id: string) => Promise<void>; onNew: () => void; onClose: () => void; onSignOut: () => void }) {
  const { t, i18n } = useTranslation();
  const [tab, setTab] = useState<Tab>(workbookId ? 'workbook' : 'all');
  const [listing, setListing] = useState<ThreadListing | null>(null);
  const [query, setQuery] = useState('');
  const empty: ThreadListing = { own: [], ancestors: [], all: [] };
  const load = () => apiService.listThreads(workbookId, { all: tab === 'all' }).then(setListing).catch(() => setListing(empty));
  useEffect(() => {
    setListing(null);
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workbookId, tab]);

  const matches = (th: ThreadSummary) => !query.trim() || (th.title ?? '').toLowerCase().includes(query.trim().toLowerCase());
  const groups = useMemo(() => groupThreads(((tab === 'all' ? listing?.all : listing?.own) ?? []).filter(matches)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [listing, query, tab]);
  const ancestors: AncestorThreads[] = useMemo(() => (tab === 'workbook' ? (listing?.ancestors ?? []) : [])
    .map((a) => ({ ...a, threads: a.threads.filter(matches) })).filter((a) => a.threads.length),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [listing, query, tab]);

  const [row, setRow] = useState<RowState>(null);
  const [failed, setFailed] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // The thread being loaded: its row shows a spinner in place of the date, and no other row opens meanwhile.
  const [opening, setOpening] = useState<string | null>(null);
  const open = (id: string) => {
    if (opening) return;
    setOpening(id);
    void onOpen(id).finally(() => setOpening(null));
  };

  const run = async (id: string, action: () => Promise<void>) => {
    setBusy(true);
    setFailed(null);
    try {
      await action();
      setRow(null);
      await load();
    } catch {
      setFailed(id);
    } finally {
      setBusy(false);
    }
  };
  const saveRename = () => {
    if (row?.kind !== 'rename') return;
    const title = row.value.trim();
    if (!title) return;
    void run(row.id, () => apiService.renameThread(row.id, title.slice(0, 255)));
  };
  const confirmDelete = () => {
    if (row?.kind !== 'delete') return;
    void run(row.id, () => apiService.deleteThread(row.id));
  };

  const fileOf = (th: ThreadSummary): string | null => (tab === 'all' ? (th.workbook_name ?? t('history.noWorkbook')) : (th.workbook_name ?? workbookName));
  const renderRow = (th: ThreadSummary) => (
    <div key={th.thread_id} data-thread={th.thread_id} className={`group flex flex-col gap-[3px] rounded-md p-2 transition-colors hover:bg-hover-soft ${th.thread_id === currentThreadId || row?.id === th.thread_id ? 'bg-hover-soft' : ''}`}>
      {row?.kind === 'rename' && row.id === th.thread_id ? (
        <div className="flex items-center gap-2">
          <input
            autoFocus
            value={row.value}
            disabled={busy}
            onChange={(e) => setRow({ kind: 'rename', id: th.thread_id, value: e.target.value })}
            onKeyDown={(e) => {
              if (e.key === 'Enter') saveRename();
              if (e.key === 'Escape') setRow(null);
            }}
            placeholder={t('history.renamePrompt')}
            aria-label={t('history.renamePrompt')}
            className="min-w-0 flex-1 rounded-[4px] border border-line-soft bg-white px-2 py-1 text-[13px] text-ink focus:border-accent focus:outline-hidden"
          />
          <button type="button" disabled={busy || !row.value.trim()} onClick={saveRename} className="rounded-[4px] bg-accent px-2 py-1 text-[11px] text-white disabled:opacity-50">
            {t('history.save')}
          </button>
          <button type="button" disabled={busy} onClick={() => setRow(null)} className="text-[11px] text-muted hover:text-ink">
            {t('history.cancel')}
          </button>
        </div>
      ) : (
      <button type="button" onClick={() => open(th.thread_id)} className="flex flex-col gap-[3px] text-left">
        <div className="flex items-baseline justify-between gap-2">
          <span className="truncate text-[13px] font-medium tracking-[-0.005em] text-ink">{th.title || t('history.untitled')}</span>
          {opening === th.thread_id
            ? <span className="flex h-[14px] shrink-0 items-center" role="status" aria-label={t('history.opening')}><Delayed className="inline-flex"><Spinner /></Delayed></span>
            : <span className="shrink-0 font-mono text-[10.5px] text-fainter">{formatWhen(th.updated_at, i18n.language)}</span>}
        </div>
        <div className="flex justify-between gap-2">
          <span className="truncate text-[12px] text-muted">{t(`mode.${th.mode}`)}</span>
          <span className="flex shrink-0 items-baseline gap-1.5 truncate font-mono text-[10.5px]">
            {th.origin && <span className="text-fainter">{t('history.resumed')}</span>}
            {!th.writable && <span className="text-fainter" data-readonly>{t('history.readOnly')}</span>}
            {fileOf(th) && <span className="truncate text-accent">{fileOf(th)}</span>}
          </span>
        </div>
        {!!th.agents?.length && (
          <div className="flex flex-wrap gap-1 pt-0.5">
            {th.agents.filter((a) => a !== 'main').map((a) => (
              <AgentTag key={a} agent={a} t={t} />
            ))}
          </div>
        )}
      </button>
      )}
      {row?.kind === 'delete' && row.id === th.thread_id ? (
        <div className="flex items-center justify-end gap-3 text-[11px]">
          <span className="text-ink">{t('history.deleteConfirm')}</span>
          <button type="button" disabled={busy} onClick={confirmDelete} className="rounded-[4px] bg-danger px-2 py-0.5 text-white disabled:opacity-50">
            {t('history.confirmDelete')}
          </button>
          <button type="button" disabled={busy} onClick={() => setRow(null)} className="text-muted hover:text-ink">
            {t('history.cancel')}
          </button>
        </div>
      ) : row?.kind === 'rename' && row.id === th.thread_id ? null : (
        <div className="hidden justify-end gap-3 text-[11px] group-focus-within:flex group-hover:flex">
          <button type="button" onClick={() => { setFailed(null); setRow({ kind: 'rename', id: th.thread_id, value: th.title ?? '' }); }} className="text-muted hover:text-ink">
            {t('history.rename')}
          </button>
          <button type="button" onClick={() => { setFailed(null); setRow({ kind: 'delete', id: th.thread_id }); }} className="text-muted hover:text-danger">
            {t('history.delete')}
          </button>
        </div>
      )}
      {failed === th.thread_id && <p role="alert" className="text-right text-[11px] text-danger">{t('history.actionFailed')}</p>}
    </div>
  );

  return (
    <div className="flex h-full min-h-0 flex-col bg-pane">
      <Header
        left={
          <>
            <button type="button" onClick={onClose} className="text-[14px] text-muted hover:text-ink" title={t('history.back')} aria-label={t('history.back')}>
              ←
            </button>
            <span className="text-[13px] font-semibold tracking-[-0.01em] text-ink">{t('history.title')}</span>
          </>
        }
        right={<GlyphButton glyph="+" title={t('header.newChat')} onClick={onNew} size={16} />}
      />
      {workbookId && (
        <div className="flex gap-1 px-3.5 pt-3" role="tablist">
          {(['workbook', 'all'] as const).map((k) => (
            <button key={k} type="button" role="tab" aria-selected={tab === k} data-tab={k} onClick={() => setTab(k)}
              className={`rounded-[4px] px-2 py-1 text-[12px] ${tab === k ? 'bg-hover-soft font-medium text-ink' : 'text-muted hover:text-ink'}`}>
              {t(k === 'workbook' ? 'history.thisWorkbook' : 'history.all')}
            </button>
          ))}
        </div>
      )}
      <div className="px-3.5 pb-1 pt-3">
        <input
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={t('history.search')}
          className="w-full rounded-md border border-line-btn bg-white px-2.5 py-[7px] text-[13px] text-ink placeholder:text-fainter focus:border-accent focus:outline-hidden focus:ring-0"
        />
      </div>
      <div className="flex-1 overflow-y-auto px-1.5 py-2">
        {listing === null ? (
          <p className="p-3 text-center text-[12px] text-fainter">{t('app.loading')}</p>
        ) : groups.every((g) => !g.items.length) && !ancestors.length ? (
          <p className="p-3 text-center text-[12px] text-fainter">{query ? t('history.noMatch') : t(tab === 'all' ? 'history.emptyAll' : 'history.empty')}</p>
        ) : (
          <>
            {groups.map((g) =>
              g.items.length ? (
                <div key={g.key} className="flex flex-col">
                  <div className="px-2 pb-1.5 pt-3 font-mono text-[11px] uppercase tracking-[0.06em] text-faint">{t(`history.groups.${g.key}`)}</div>
                  {g.items.map((th) => renderRow(th))}
                </div>
              ) : null,
            )}
            {ancestors.map((a) => (
              <div key={`${a.name}-${a.forked_at}`} className="flex flex-col" data-ancestor={a.name}>
                <div className="px-2 pb-1.5 pt-3 font-mono text-[11px] uppercase tracking-[0.06em] text-faint">{t('history.ancestor', { name: a.name })}</div>
                {a.threads.map((th) => renderRow(th))}
              </div>
            ))}
          </>
        )}
      </div>
      <div className="flex shrink-0 items-center justify-between border-t border-line-soft px-3.5 py-2.5 text-[12px]">
        <button type="button" onClick={() => void i18n.changeLanguage(i18n.language === 'fr' ? 'en' : 'fr')} className="font-mono text-[11px] text-faint hover:text-ink" title={t('header.language')}>
          {i18n.language === 'fr' ? 'EN' : 'FR'}
        </button>
        <button type="button" onClick={onSignOut} className="text-muted hover:text-ink">
          {t('header.logout')}
        </button>
      </div>
    </div>
  );
}

export function groupThreads(threads: ThreadSummary[], now = new Date()): { key: GroupKey; items: ThreadSummary[] }[] {
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  const day = 86_400_000;
  const buckets: Record<GroupKey, ThreadSummary[]> = { today: [], yesterday: [], lastWeek: [], earlier: [] };
  for (const th of threads) {
    const ts = new Date(th.updated_at).getTime();
    const key: GroupKey = ts >= startOfToday ? 'today' : ts >= startOfToday - day ? 'yesterday' : ts >= startOfToday - 7 * day ? 'lastWeek' : 'earlier';
    buckets[key].push(th);
  }
  return GROUP_ORDER.map((key) => ({ key, items: buckets[key] }));
}

/** HH:MM today, weekday within the last week, short date otherwise. */
export function formatWhen(iso: string, locale: string, now = new Date()): string {
  const d = new Date(iso);
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  if (d.getTime() >= startOfToday) return d.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' });
  if (d.getTime() >= startOfToday - 7 * 86_400_000) return d.toLocaleDateString(locale, { weekday: 'short' });
  return d.toLocaleDateString(locale, { day: 'numeric', month: 'short' });
}
